import React, { useEffect, useRef, useState } from 'react';
import MeasurementUnits from '../../Layout/MeasurementUnits';
import { formatItemName } from './helpers';
import type { TrackerShoppingListItem as Item } from './types';

/** Matches the `transition: all 0.3s` of `.ingredients li > button` in `_shoppingList.scss`. */
export const HIDE_ANIMATION_MS = 300;

interface Props {
  item: Item;
  /** Effective state — may differ from `item.isBought` while a mutation is in flight. */
  isBought: boolean;
  onToggle: (item: Item) => void;
}

const getClassName = (isBought: boolean, isHiding: boolean) => {
  if (!isHiding) return isBought ? 'done show' : 'show';
  return isBought ? 'hide-up' : 'done hide-down';
};

/**
 * Same animation as the meal-plan `ShoppingListProduct`: the row slides out (down when marked,
 * up when unmarked) and only then is reported, so it reappears in the other section with the
 * `show` keyframes once it remounts there.
 */
const TrackerShoppingListItem: React.FC<Props> = ({ item, isBought, onToggle }) => {
  const [isHiding, setIsHiding] = useState(false);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const designSystem = localStorage.getItem('developerOptionsDesignSystem') || 'brand';

  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  const handleClick = () => {
    if (isHiding) return;

    setIsHiding(true);
    hideTimeout.current = setTimeout(() => {
      setIsHiding(false);
      onToggle(item);
    }, HIDE_ANIMATION_MS);
  };

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        className={getClassName(isBought, isHiding)}
        aria-pressed={isBought}
        data-design-system={designSystem}
        data-testid="tracker-shopping-list-item"
      >
        {formatItemName(item)}
        {/* Same component as the meal-plan list, so rounding and separators match. */}
        <MeasurementUnits
          measurements={[{ amount: item.baseAmount, unit: item.baseUnit, text: '' }]}
        />
      </button>
    </li>
  );
};

export default TrackerShoppingListItem;
